/**
 * Stops the Shopify Inbox launcher bouncing.
 *
 * Shopify runs `nudge-bounce 10s infinite` on `.activator-nudge` inside
 * <shopify-chat>'s shadow root. That wrapper carries no `part`, so it cannot be
 * selected from the page, and the obvious fix — zeroing the keyframes'
 * --private-nudge-bounce-height from outside — does not work either: the
 * component declares that property on an element INSIDE the shadow tree, and an
 * inherited value from the host is the weakest thing in the cascade. Measured:
 * still 5px with the host override in place.
 *
 * The shadow root is open, so the remaining option is to append a stylesheet to
 * it. Deliberately narrow — it disables one animation and nothing else.
 *
 * If Shopify ever renames the class, the selector stops matching and the bounce
 * comes back. That is the failure mode: it degrades to today's behaviour.
 */
(function () {
  // The activator's label and icon are plain elements inside the shadow tree —
  // neither is exposed as a part — so the collapse-to-circle behaviour and the
  // icon swap can only be done from in here. Colour, radius and sizing live in
  // card-slinger-brand.css via ::part(activator), which keeps working on its
  // own if this injection ever stops matching.
  //
  // Shopify's default glyph is a thin outlined bubble. This replaces it with a
  // classic solid speech bubble, painted as a mask so it still takes its colour
  // from the button (and therefore inverts on hover).
  const BUBBLE =
    "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath fill='%23000' d='M20 2H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h4v4l5-4h7a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2z'/%3E%3C/svg%3E\")";

  const CSS = `
    .activator-nudge, .activator-nudge--active {
      animation: none !important;
      translate: none !important;
    }

    .activator {
      /* Gap is constant. Animating it as well as the width gave the transition
         two things to fight over and made the open feel stepped. At rest the
         label and this gap simply overflow the 56px button and are clipped. */
      column-gap: 8px !important;
      row-gap: 0 !important;
    }

    .activator__icon {
      width: 24px !important;
      height: 24px !important;
      /* color:inherit first. The icon span is not a part, so it never sees the
         white we set via ::part(activator) — it keeps taking the component's
         --activator-fg, which our --shopify-chat-text-color pins to ink. That
         made currentColor resolve to ink-on-ink: the button looked empty. */
      color: inherit !important;
      background-color: currentColor !important;
      -webkit-mask: ${BUBBLE} center / contain no-repeat;
      mask: ${BUBBLE} center / contain no-repeat;
    }
    .activator__icon svg { display: none !important; }

    /* Fades and slides in slightly behind the pill opening, rather than driving
       the width itself. transition-delay on hover only, so it leaves instantly. */
    .activator__label {
      /* Same trap as the icon: the component sets
         .activator__label { color: var(--activator-fg) }, which our
         --shopify-chat-text-color pins to ink for the panel. The white on the
         button comes from ::part(activator), which the label never sees — so
         the text rendered ink on the purple pill. */
      color: inherit !important;
      opacity: 0 !important;
      transform: translateX(-6px);
      white-space: nowrap;
      transition: opacity 160ms ease-out, transform 260ms cubic-bezier(0.83, 0, 0.17, 1) !important;
    }
    .activator:hover .activator__label,
    .activator:focus-visible .activator__label {
      opacity: 1 !important;
      transform: none;
      transition-delay: 90ms !important;
    }

    /* The unread dot is a third grid column after the label, and the component
       paints it with background-color: var(--activator-fg) — the same ink our
       --shopify-chat-text-color pins for the panel — so it rendered black on
       the black button. Same trap as the icon and label above, same fix. */
    .activator__indicator {
      color: inherit !important;
      background-color: currentColor !important;
    }

    /* 96px was sized for the icon, one 8px gap and "Chat". The dot adds a third
       column — another 8px gap plus its 6px — and the grid had nowhere to put
       it: measured at x:1250 in a button whose content box ended at 1245, so it
       sat out in the right padding against the pill's curve. 112px buys back
       exactly that, and only while there is something unread, so the button
       keeps its 96px shape the rest of the time. */
    .activator:hover:has(.activator__indicator--active),
    .activator:focus-visible:has(.activator__indicator--active) {
      width: 112px !important;
    }
  `;

  // The launcher does not stay where it starts. On first paint the activator is
  // a direct child of <shopify-chat>'s shadow root, but the moment the chat is
  // opened the component mounts <shopify-agent-runtime> and re-creates the
  // activator inside THAT element's shadow root — one boundary deeper — and
  // leaves it there for the rest of the page's life.
  //
  // Stylesheets do not cross shadow boundaries, so a sheet appended only to the
  // outer root silently stops applying. Measured after one open/close: the
  // label computed rgb(10,10,10) and the icon ink at 70% — ink on the ink
  // button, so both simply vanished. The notification and just-closed states
  // are where it showed, because those are what widen the pill far enough to
  // reveal the label.
  //
  // ::part(activator) survives the move — the runtime re-exports it
  // (exportparts="activator, dialog") — which is why the button keeps its ink
  // fill and white border while its contents go dark. Only the rules that have
  // to live inside the shadow tree are lost.
  //
  // Pinning --activator-fg on the host does not help: like
  // --private-nudge-bounce-height above, the component declares it on an
  // element inside the tree, so the host's inherited value loses the cascade.
  // Measured — setting it white left the label at rgb(10,10,10). Getting the
  // sheet into the new root is the only lever that works.
  const observed = new WeakSet();

  const visit = (root) => {
    if (root.querySelector('.activator') && !root.querySelector('style[data-cs-chat]')) {
      const style = document.createElement('style');
      style.setAttribute('data-cs-chat', '');
      style.textContent = CSS;
      root.appendChild(style);
    }

    // Observe every root reached, not just the outer one. The activator is
    // rendered into the runtime's root some time after that element is
    // inserted, so an observer on the outer root alone fires too early to see
    // it. Re-entry is safe: appending the sheet trips the observer once more,
    // the guard above finds it already there, and the pass ends.
    if (!observed.has(root)) {
      observed.add(root);
      new MutationObserver(() => apply()).observe(root, { childList: true, subtree: true });
    }

    root.querySelectorAll('*').forEach((el) => el.shadowRoot && visit(el.shadowRoot));
  };

  const apply = () => {
    const root = document.querySelector('shopify-chat')?.shadowRoot;
    if (!root) return false;
    visit(root);
    return true;
  };

  if (!apply()) {
    // The element upgrades and attaches its shadow root asynchronously, so poll
    // briefly rather than racing it. Once the root exists the observers take
    // over and this stops. Gives up after ~15s.
    let tries = 0;
    const timer = setInterval(() => {
      if (apply() || ++tries > 60) clearInterval(timer);
    }, 250);
  }
})();
